import { LoadingSpinner } from "components/LoadingSpinner";
import { centsToDollars } from "helpers/price";
import { useEffect, useRef } from "react";
import {
  useLazyGetImplantSuggestionQuery,
  IAmountDistributionEntry,
  IDistributionWithRange,
} from "store/queries/implantSuggestions";
import styled from "styled-components";

const SuggestionContainer = styled.div`
  margin-top: 8px;
  padding: 12px 16px;
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 6px;
`;

const SuggestionHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 8px;
`;

const SuggestionRow = styled.div`
  font-size: 13px;
  color: #334155;
  line-height: 1.6;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  color: #64748b;
`;

const Value = styled.span`
  font-weight: 500;
  color: #1e293b;
`;

const PercentageTag = styled.span`
  font-weight: 600;
  color: #059669;
`;

const AmountList = styled.span`
  color: #475569;
`;

/**
 * Format cents to dollars with proper formatting
 */
function formatAmount(cents: number): string {
  return `$${centsToDollars(cents).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format a percentage (0-1) to display string
 */
function formatPercentage(pct: number): string {
  return `${Math.round(pct * 100)}%`;
}

/**
 * Format distribution entries for display
 * Shows amounts with their percentages
 */
function formatDistributionEntries(
  entries: IAmountDistributionEntry[]
): string {
  if (!entries || entries.length === 0) {
    return "No data";
  }

  return entries
    .map(
      (entry) =>
        `${formatAmount(entry.amount)} (${formatPercentage(entry.percentage)})`
    )
    .join(", ");
}

/**
 * Format a distribution - either as entries or as a range if highly variable
 */
function formatDistribution(dist: IDistributionWithRange): string {
  // If we have entries, show them
  if (dist.entries && dist.entries.length > 0) {
    return formatDistributionEntries(dist.entries);
  }

  // If we have a range, show min-max
  if (dist.minAmount !== undefined && dist.maxAmount !== undefined) {
    return `varies significantly (${formatAmount(
      dist.minAmount
    )} – ${formatAmount(dist.maxAmount)})`;
  }

  return "No data";
}

/**
 * Check if distribution has data (either entries or range)
 * Type guard that narrows the type to IDistributionWithRange
 */
function hasDistributionData(
  dist: IDistributionWithRange | null | undefined
): dist is IDistributionWithRange {
  if (!dist) return false;
  return (
    (dist.entries && dist.entries.length > 0) ||
    (dist.minAmount !== undefined && dist.maxAmount !== undefined)
  );
}

interface IImplantSuggestionProps {
  cptCodes: string[];
  // Optional quantities map - used to detect quantity changes and trigger refetch
  cptQuantities?: Record<string, number>;
  payerId?: number;
  payerName?: string;
  accountId?: string;
  isLoading?: boolean;
  // When true, delays fetching until input loses focus (reduces UI jitter during rapid entry)
  isCptInputFocused?: boolean;
  // Props accepted for parent component compatibility but not used
  // The suggestion display is now informational only (no Add/Dismiss buttons)
  onAddImplant?: (implantCode: string, suggestedAmount?: number) => void;
  disabled?: boolean;
}

export function ImplantSuggestion({
  cptCodes,
  cptQuantities,
  payerId,
  payerName,
  accountId,
  isLoading: externalLoading = false,
  isCptInputFocused = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAddImplant,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  disabled,
}: IImplantSuggestionProps) {
  const [getSuggestion, { data, isLoading, isFetching }] =
    useLazyGetImplantSuggestionQuery();

  // Track what inputs the current data was fetched for
  // This prevents showing stale data when inputs change
  const lastFetchedInputsRef = useRef<string>("");
  const lastPayerIdRef = useRef<number | undefined>(payerId);
  const currentInputsKey = JSON.stringify({
    cptCodes: [...cptCodes].sort(),
    cptQuantities,
    payerId,
    accountId,
  });

  // Fetch suggestion when:
  // 1. CPT input loses focus (blur) and inputs have changed
  // 2. Payer changes (immediate, regardless of focus)
  // 3. Initial load when not focused
  useEffect(() => {
    if (cptCodes.length === 0) {
      return;
    }

    // Always fetch immediately if payer changed
    const payerChanged = lastPayerIdRef.current !== payerId;
    lastPayerIdRef.current = payerId;

    // Skip fetch if input is focused (unless payer changed)
    if (isCptInputFocused && !payerChanged) {
      return;
    }

    // Skip if nothing changed
    if (lastFetchedInputsRef.current === currentInputsKey) {
      return;
    }

    const timeoutId = setTimeout(() => {
      // Mark what inputs we're fetching for
      lastFetchedInputsRef.current = currentInputsKey;
      getSuggestion({
        cptCodes,
        payerId,
        accountId,
      });
    }, 300); // Shorter debounce since we're already waiting for blur

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [
    currentInputsKey,
    getSuggestion,
    cptCodes,
    payerId,
    accountId,
    isCptInputFocused,
  ]);

  // Data is stale if inputs changed since last fetch
  const isDataStale = lastFetchedInputsRef.current !== currentInputsKey;

  // Don't show if no CPT codes selected
  if (cptCodes.length === 0) {
    return null;
  }

  // Don't show if loading externally
  if (externalLoading) {
    return null;
  }

  // When input is focused and data is stale, hide completely (user is still entering CPT codes)
  if (isCptInputFocused && isDataStale) {
    return null;
  }

  // Show loading spinner while fetching or when data is stale (waiting for fetch after blur)
  if (isLoading || isFetching || isDataStale) {
    return (
      <SuggestionContainer>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <LoadingSpinner
            customStyle={{
              width: "20px",
              height: "20px",
            }}
          />
          <span style={{ color: "#0369a1", fontSize: 14 }}>
            Checking for implant suggestions...
          </span>
        </div>
      </SuggestionContainer>
    );
  }

  // Don't show if no suggestion
  if (!data?.suggestion) {
    return null;
  }

  const { suggestion } = data;
  const { chargeDistribution, payerDistributions } = suggestion;

  return (
    <SuggestionContainer>
      {/* Header: Most likely implant */}
      <SuggestionHeader>
        <PercentageTag>
          {formatPercentage(suggestion.associationPercentage)}
        </PercentageTag>
        {" of similar cases include implant "}
        <Value>{suggestion.implantCptCode}</Value>
      </SuggestionHeader>

      {/* Likely charge amount (global) */}
      {hasDistributionData(chargeDistribution) && (
        <SuggestionRow>
          <Label>Likely charge amount: </Label>
          <AmountList>{formatDistribution(chargeDistribution)}</AmountList>
        </SuggestionRow>
      )}

      {/* Payer-specific data */}
      {payerDistributions && (
        <>
          {/* Payer-specific header */}
          <SuggestionRow>
            <Label style={{ fontWeight: 500 }}>
              Based on {payerDistributions.payerName} history:
            </Label>
          </SuggestionRow>

          {/* Likely insurance payment */}
          {hasDistributionData(payerDistributions.paidAmount) && (
            <SuggestionRow style={{ marginLeft: 12 }}>
              <Label>Likely insurance payment: </Label>
              <AmountList>
                {formatDistribution(payerDistributions.paidAmount)}
              </AmountList>
            </SuggestionRow>
          )}

          {/* Likely patient OOP */}
          {hasDistributionData(payerDistributions.patientOop) && (
            <SuggestionRow style={{ marginLeft: 12 }}>
              <Label>Likely patient responsibility: </Label>
              <AmountList>
                {payerDistributions.patientOop.entries.length > 0 ? (
                  payerDistributions.patientOop.entries.map((entry, idx) => (
                    <span key={`oop-${entry.amount}-${idx}`}>
                      {idx > 0 && ", "}
                      {formatPercentage(entry.percentage)} of the time patient
                      owes ~{formatAmount(entry.amount)}
                    </span>
                  ))
                ) : (
                  <span>
                    {formatDistribution(payerDistributions.patientOop)}
                  </span>
                )}
              </AmountList>
            </SuggestionRow>
          )}
        </>
      )}

      {/* Show payer name hint if no payer-specific data but payer was requested */}
      {!payerDistributions && payerName && (
        <SuggestionRow>
          <Label style={{ fontStyle: "italic" }}>
            Limited or no historical data available for {payerName}
          </Label>
        </SuggestionRow>
      )}
    </SuggestionContainer>
  );
}
